// Request
import request from '../../request/effects'
// Logs
import { logManager } from '../../logs'
const log = logManager.getLogger('NOTIFICATIONS')

/**
 * Registers a device token with On-Prem services.
 * @method pushNotificationsRegistration
 * @param  {Object} connection - Information for formatting the request.
 * @param  {Object} options
 * @param  {string[]} options.services - Push service to register to; either 'google' or 'apple'.
 * @param {string} options.pushProvider - The push provider, can be either 'apple' or 'google'.
 * @param  {string} options.bundleId - The bundle id to use for registration.
 * @param  {string} options.deviceToken
 * @param {string} options.voipDeviceToken - Token for call related service.
 * @param {string} options.standardDeviceToken - Token for non-call related service.
 * @param  {string} options.clientCorrelator - Unique identifier for a client device.
 * @param {string} options.realm - The realm used by the push registration service to identify
 *                                and establish a connection with the service gateway.
 * @param {boolean} options.isProduction - If true, push notification will be sent to production. If false, push notification will be sent to sandbox.
 * @return {Object} response
 */
export function * pushNotificationsRegistration (
  connection,
  {
    services,
    pushProvider,
    bundleId,
    deviceToken,
    voipDeviceToken,
    standardDeviceToken,
    clientCorrelator,
    realm,
    isProduction
  }
) {
  const { server } = connection
  pushProvider = pushProvider.toLowerCase()
  const url =
    `${server.protocol}://${server.server}:${server.port}/` +
    `rest/version/${server.version}/` +
    `user/${connection.username}/` +
    'push/' +
    pushProvider +
    '/devices/'
  const method = 'POST'
  let body = {
    bundleID: bundleId,
    service: services,
    clientCorrelator: clientCorrelator,
    realm: realm,
    isProduction: isProduction
  }

  if (pushProvider === 'apple') {
    body.voipDeviceToken = voipDeviceToken
    body.standardDeviceToken = standardDeviceToken
  } else {
    body.deviceToken = deviceToken
  }

  body = JSON.stringify(body)
  log.debug(`Sending PUSH register request: ${method} ${url}`)

  const response = yield request({ url, method, body })

  let registrationResponse
  const responseName = pushProvider + 'DeviceRegistrationResponse'
  if (response.payload.body && response.payload.body[responseName]) {
    registrationResponse = response.payload.body[responseName]
  }

  if (response.error) {
    log.info('Failed to register device token for PUSH notifications.')
    if (registrationResponse) {
      // Handle errors from the server.
      const statusCode = registrationResponse.statusCode
      log.debug(`Device registration request for PUSH notifications failed with status code: ${statusCode}`)

      return {
        error: true,
        status: statusCode,
        text: `Failed to register device token for PUSH notifications. Code: ${statusCode}`
      }
    } else {
      // Handle errors from the request helper.
      const { message } = response.payload.result
      log.debug(`Device registration request for PUSH notifications failed with message: ${message}.`)

      return {
        error: true,
        status: response.payload.result.code,
        text: `Failed to register device token for PUSH notifications. Error: ${response.payload.result.message}`
      }
    }
  } else if (registrationResponse && registrationResponse.statusCode !== 0) {
    log.info(`Failed to register device token for PUSH notifications. Code: ${registrationResponse.statuscode}`)
    // TODO: Is this else-if needed?
    return {
      error: true,
      status: registrationResponse.statuscode,
      text: `Failed to register device token for PUSH notifications. Error: ${registrationResponse.statusCode}`
    }
  } else {
    log.info('Successfully registered device token for PUSH notifications.')
    return {
      error: false,
      ...registrationResponse
    }
  }
}

/**
 * De-Registers a device token with On-Prem services.
 * @method pushNotificationsDeRegistration
 * @param  {Object} connection - Information for formatting the request.
 * @param  {Object} options
 * @param  {string} options.registration
 * @return {Object} response
 */
export function * pushNotificationsDeRegistration (connection, { registration }) {
  const { server } = connection
  const url = `${server.protocol}://${server.server}:${server.port}${registration}`
  const method = 'DELETE'
  const responseType = 'none'

  log.debug(`Sending PUSH unregister request: ${method} ${url}`)
  const response = yield request({ url, method, responseType })

  if (response.error) {
    log.info('Failed to unregister device token for PUSH notifications.')
    if (response.payload.body) {
      // Handle errors from the server.
      const { statusCode } = response.payload.body
      log.debug(`Failed to unregister device token for PUSH notifications. Status code: ${statusCode}.`)
      // TODO: Proper errors.
      return {
        error: true,
        status: statusCode,
        text: `Failed to unregister device token for PUSH notifications. Code: ${statusCode}.`
      }
    } else {
      // Handle errors from the request helper.
      const { message } = response.payload.result
      log.debug(`Device token unregistration request failed for PUSH notifications: ${message}`)
      // TODO: Proper error.
      return {
        error: true,
        status: response.payload.result.code,
        text: `Device token unregistration request failed for PUSH notifications: ${message}`
      }
    }
  } else {
    log.info('Successfully unregistered device token for PUSH notifications.')
    // Successful de-register has no response.
    return {
      error: false
    }
  }
}

/**
 * Fetches SDP data from a given partial URL.
 * @method fetchSDP
 * @param  {Object} partialUrl - A partial URL. Contains everything after the protocol://server:port
 * @return {Object} response A response payload
 */
export function * fetchSDP (connection, partialUrl) {
  const { server } = connection
  const method = 'GET'
  const url = `${server.protocol}://${server.server}:${server.port}${partialUrl}`
  log.debug(`Sending request to fetch SDP: ${method} ${url}`)

  const response = yield request({
    url,
    method
  })

  if (!response.error) {
    log.info('SDP fetched successfully.')
    // TODO: test and see what this format actually is.
    return response.payload.body
  } else {
    log.debug(`Failed to fetch SDP. Error is: ${JSON.stringify(response.error)}`)
  }
}
